import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { useMemo, useState } from "react";
import type { buildCourseRequest } from "../utils/buildCourseRequest";

type ParsedCourseRequest = Awaited<ReturnType<typeof buildCourseRequest>>;

type Props = {
  open: boolean;
  onClose: () => void;
  data: ParsedCourseRequest | null;
};

export function CoursePreviewModal({ open, onClose, data }: Props) {
  const [tab, setTab] = useState(0);

  const counts = useMemo(
    () => ({
      modules: data?.modules.length ?? 0,
      submodules: data?.targets.length ?? 0,
      questions: data?.questions.length ?? 0,
    }),
    [data]
  );

  const targetsByModule = useMemo(() => {
    const map = new Map<string, any[]>();
    if (!data) return map;
    for (const t of data.targets) {
      const arr = map.get(t.module_id) ?? [];
      arr.push(t);
      map.set(t.module_id, arr);
    }
    return map;
  }, [data]);

  const questionsByTarget = useMemo(() => {
    const map = new Map<string, any[]>();
    if (!data) return map;
    for (const q of data.questions) {
      const arr = map.get(q.target_id) ?? [];
      arr.push(q);
      map.set(q.target_id, arr);
    }
    return map;
  }, [data]);

  if (!data) return null;

  const course: any = data.course[0] ?? {};
  const meta = (course.metadata ?? {}) as Record<string, any>;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Course Preview
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "grey.50" }}>
        {/* -- Header card -- */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Course
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {course.name || "(unnamed)"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID:{" "}
                  {course.course_id !== undefined
                    ? course.course_id
                    : "(auto-generated on import)"}
                </Typography>
              </Box>
              <Chip
                label={course.higher_ed ? "Higher Ed" : "K-12"}
                color={course.higher_ed ? "primary" : "default"}
                size="small"
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" gap={1} flexWrap="wrap">
              {meta.deeplink_asset_type && (
                <Chip
                  label={`Deeplink: ${meta.deeplink_asset_type}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {meta.lti_grading_method && (
                <Chip
                  label={`Method: ${meta.lti_grading_method}`}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                label={`Passback: ${
                  meta.lti_block_grade_passback ? "blocked" : "enabled"
                }`}
                size="small"
                variant="outlined"
                color={meta.lti_block_grade_passback ? "warning" : "success"}
              />
              {meta.lti_grading_trigger && (
                <Chip
                  label={`Trigger: ${meta.lti_grading_trigger}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>

            <Stack direction="row" gap={4} mt={2}>
              <CountBlock label="Modules" value={counts.modules} />
              <CountBlock label="Submodules" value={counts.submodules} />
              <CountBlock label="Questions" value={counts.questions} />
            </Stack>
          </CardContent>
        </Card>

        {/* -- Tabs -- */}
        <Card variant="outlined">
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
              <Tab label="Hierarchy" />
              <Tab label="Course" />
              <Tab label={`Modules (${counts.modules})`} />
              <Tab label={`Submodules (${counts.submodules})`} />
              <Tab label={`Questions (${counts.questions})`} />
            </Tabs>
          </Box>
          <Box sx={{ p: 2 }}>
            {tab === 0 && (
              <HierarchyView
                modules={data.modules}
                targetsByModule={targetsByModule}
                questionsByTarget={questionsByTarget}
              />
            )}
            {tab === 1 && <CourseDetails course={course} meta={meta} />}
            {tab === 2 && <ModulesTable rows={data.modules} />}
            {tab === 3 && <SubmodulesTable rows={data.targets} />}
            {tab === 4 && <QuestionsTable rows={data.questions} />}
          </Box>
        </Card>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function CountBlock({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function HierarchyView({
  modules,
  targetsByModule,
  questionsByTarget,
}: {
  modules: any[];
  targetsByModule: Map<string, any[]>;
  questionsByTarget: Map<string, any[]>;
}) {
  if (!modules.length) return <EmptyState text="No modules" />;
  return (
    <Box>
      {modules.map((m) => {
        const targets = targetsByModule.get(m.module_id) ?? [];
        return (
          <Accordion key={m.module_id} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" gap={1} flex={1}>
                <Typography fontWeight={600}>{m.name || m.module_id}</Typography>
                <Chip label={`${targets.length} submodules`} size="small" />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pl: 4, bgcolor: "grey.50" }}>
              {targets.length === 0 ? (
                <EmptyState text="No submodules" />
              ) : (
                targets.map((t) => {
                  const qs = questionsByTarget.get(t.target_id) ?? [];
                  return (
                    <Accordion key={t.target_id} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" alignItems="center" gap={1} flex={1}>
                          <Typography>{t.name || t.target_id}</Typography>
                          <Chip
                            label={`${qs.length} questions`}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pl: 4 }}>
                        {qs.length === 0 ? (
                          <EmptyState text="No questions" />
                        ) : (
                          qs.map((q) => (
                            <Stack
                              key={q.problem_id}
                              direction="row"
                              alignItems="center"
                              gap={1}
                              py={0.5}>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {q.resource_link_title || q.problem_id}
                              </Typography>
                              <Chip label={q.type} size="small" />
                              <Chip
                                label={q.activity_type}
                                size="small"
                                variant="outlined"
                              />
                            </Stack>
                          ))
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

function CourseDetails({ course, meta }: { course: any; meta: Record<string, any> }) {
  // Sheet-derived metadata keys are highlighted so admins can distinguish them
  // from keys inherited via the template-course picker.
  const SHEET_DERIVED_KEYS = new Set([
    "deeplink_asset_type",
    "lti_grading_method",
    "lti_block_grade_passback",
    "lti_grading_trigger",
  ]);

  const topRows: [string, any][] = [
    ["course_id", course.course_id ?? "(auto-generated on import)"],
    ["name", course.name],
    ["higher_ed", String(course.higher_ed)],
  ];

  const metaEntries = Object.entries(meta).sort(([a], [b]) => {
    const aSheet = SHEET_DERIVED_KEYS.has(a) ? 0 : 1;
    const bSheet = SHEET_DERIVED_KEYS.has(b) ? 0 : 1;
    if (aSheet !== bSheet) return aSheet - bSheet;
    return a.localeCompare(b);
  });

  const formatValue = (v: any) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") return JSON.stringify(v, null, 2);
    return String(v);
  };

  return (
    <Stack gap={2}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableBody>
            {topRows.map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ fontWeight: 600, width: "45%" }}>{k}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>
                  {String(v ?? "")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box>
        <Stack direction="row" alignItems="center" gap={1} mb={1} flexWrap="wrap">
          <Typography variant="subtitle2" fontWeight={600}>
            metadata
          </Typography>
          <Chip
            label={`${metaEntries.length} key${metaEntries.length === 1 ? "" : "s"}`}
            size="small"
          />
          <Chip label="● sheet-derived" size="small" color="primary" variant="outlined" />
          <Chip label="○ inherited" size="small" variant="outlined" />
        </Stack>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableBody>
              {metaEntries.length === 0 && (
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      (no metadata)
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {metaEntries.map(([k, v]) => {
                const isSheet = SHEET_DERIVED_KEYS.has(k);
                return (
                  <TableRow key={k}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: "40%",
                        verticalAlign: "top",
                        color: isSheet ? "primary.main" : "text.primary",
                      }}>
                      {isSheet ? "● " : "○ "}
                      {k}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        verticalAlign: "top",
                      }}>
                      {formatValue(v)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
}

function ModulesTable({ rows }: { rows: any[] }) {
  if (!rows.length) return <EmptyState text="No modules" />;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>module_id</TableCell>
            <TableCell>name</TableCell>
            <TableCell align="right">order</TableCell>
            <TableCell>landing_page</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.module_id}>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                {r.module_id}
              </TableCell>
              <TableCell>{r.name}</TableCell>
              <TableCell align="right">{r.order}</TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                {r.landing_page}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SubmodulesTable({ rows }: { rows: any[] }) {
  if (!rows.length) return <EmptyState text="No submodules" />;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>target_id</TableCell>
            <TableCell>module_id</TableCell>
            <TableCell>name</TableCell>
            <TableCell align="right">order</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.target_id}>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                {r.target_id}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                {r.module_id}
              </TableCell>
              <TableCell>{r.name}</TableCell>
              <TableCell align="right">{r.order}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function QuestionsTable({ rows }: { rows: any[] }) {
  if (!rows.length) return <EmptyState text="No questions" />;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>problem_id</TableCell>
            <TableCell>target_id</TableCell>
            <TableCell>type</TableCell>
            <TableCell>activity</TableCell>
            <TableCell align="right">order</TableCell>
            <TableCell>title</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.problem_id}>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                {r.problem_id}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                {r.target_id}
              </TableCell>
              <TableCell>
                <Chip label={r.type} size="small" />
              </TableCell>
              <TableCell>
                <Chip label={r.activity_type} size="small" variant="outlined" />
              </TableCell>
              <TableCell align="right">{r.order}</TableCell>
              <TableCell>{r.resource_link_title}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <Box sx={{ p: 3, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );
}
